import {
  Box,
  TextField,
  Typography,
  Stack,
  Paper,
  FormControl,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import React from 'react';
import { PackageMetadata } from '../../../interfaces/delivery-company/orders';
import InputWithLegend from '../input-with-legend/input-with-legend';
import TextFieldWithArabicKeyboard from '../text-field-with-arabic-keyboard';

interface PackagesEditorProps {
  numberOfPackages: number;
  setNumberOfPackages: (n: number) => void;
  packagesMetadata: PackageMetadata[];
  setPackagesMetadata: (
    meta: PackageMetadata[],
    n: number
  ) => void;
  disabled?: boolean;
}

const PackagesEditor: React.FC<PackagesEditorProps> = ({
  numberOfPackages,
  setNumberOfPackages,
  packagesMetadata = [] as PackageMetadata[],
  setPackagesMetadata,
  disabled = false,
}) => {
  // Ensure numberOfPackages is at least 1
  const currentPackages = Math.max(1, numberOfPackages || 1);

  // Handler to update number of packages
  const updatePackageCount = (newCount: number) => {
    // Clamp between 1 and 99
    const clampedCount = Math.max(1, Math.min(99, newCount));
    setNumberOfPackages(clampedCount);
    
    // Remove metadata for deleted packages
    const filtered = packagesMetadata.slice(0, clampedCount)
    setPackagesMetadata(filtered, clampedCount);
  };

  // Increment handler
  const handleIncrement = () => {
    if (currentPackages < 99) {
      updatePackageCount(currentPackages + 1);
    }
  };

  // Decrement handler
  const handleDecrement = () => {
    if (currentPackages > 1) {
      updatePackageCount(currentPackages - 1);
    }
  };

  // Number input handler
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Prevent more than 2 digits
    if (value.length > 2) {
      value = value.slice(0, 2);
    }
    // If the input is empty, set to 1 (minimum)
    if (value === '') {
      updatePackageCount(1);
      return;
    }
    const n = parseInt(value);
    if (!Number.isNaN(n)) {
      updatePackageCount(n);
    }
  };

  // Description input handler
  const handleDescriptionChange = (pkgNum: number, value: string) => {
    // Create a copy of the array, ensuring it has enough elements
    const updated = [...packagesMetadata];
    
    // Ensure array has enough elements (pkgNum is 1-based, so we need pkgNum elements)
    while (updated.length < pkgNum) {
      updated.push({ description: '' });
    }
    
    // Update the specific package (pkgNum is 1-based, array is 0-indexed)
    updated[pkgNum - 1] = { description: value };
    
    setPackagesMetadata(updated, currentPackages);
  };

  return (
    <Box>
      <InputWithLegend label={'Packages'} icon={null}>
        <Stack spacing={2}>
          {/* Number of Packages control with minus/plus buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 1,
            }}
          >
            <Tooltip title={currentPackages <= 1 ? 'Minimum 1 package' : 'Remove package'}>
              <span>
                <IconButton
                  onClick={handleDecrement}
                  disabled={disabled || currentPackages <= 1}
                  color="primary"
                  sx={{
                    border: '2px solid',
                    borderColor: currentPackages <= 1 ? 'action.disabled' : 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      borderColor: 'primary.dark',
                    },
                    transition: 'all 0.2s',
                  }}
                  aria-label="decrease package count"
                >
                  <RemoveIcon />
                </IconButton>
              </span>
            </Tooltip>

            <TextField
              label="Number of Packages"
              type="number"
              variant="outlined"
              required
              disabled={disabled}
              inputProps={{ 
                min: 1, 
                max: 99, 
                maxLength: 2,
                style: { textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold' }
              }}
              value={currentPackages}
              onChange={handleNumberChange}
              sx={{ 
                width: '180px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '2px',
                  },
                },
              }}
            />

            <Tooltip title={currentPackages >= 99 ? 'Maximum 99 packages' : 'Add package'}>
              <span>
                <IconButton
                  onClick={handleIncrement}
                  disabled={disabled || currentPackages >= 99}
                  color="primary"
                  sx={{
                    border: '2px solid',
                    borderColor: currentPackages >= 99 ? 'action.disabled' : 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      borderColor: 'primary.dark',
                    },
                    transition: 'all 0.2s',
                  }}
                  aria-label="increase package count"
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* All package descriptions */}
          {currentPackages > 0 && (
            <Stack spacing={2}>
              {[...Array(currentPackages)].map((_, i) => {
                const pkgNum = i + 1; // 1-based package number for display
                return (
                  <FormControl fullWidth key={pkgNum}>
                    <TextFieldWithArabicKeyboard
                      label={`Package ${pkgNum} - Optional Description`}
                      autoComplete="off"
                      type="text"
                      disabled={disabled}
                      value={packagesMetadata[i]?.description || ''}
                      onChange={(value) =>
                        handleDescriptionChange(pkgNum, value)
                      }
                      fullWidth
                      size="small"
                      multiline
                      variant="outlined"
                      rows={2}
                      sx={{
                        background: '#fff',
                      }}
                      placeholder={`Enter description for package ${pkgNum}`}
                    />
                  </FormControl>
                );
              })}
            </Stack>
          )}
        </Stack>
      </InputWithLegend>
    </Box>
  );
};

export default PackagesEditor;
